import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ConnectorTermsSnapshot, fetchAdminTerms, saveAdminTerms } from '../services/api';

const ORANGE = '#ed8936';
const TERM_BODY_MAX_CHARS = 4000;

type DraftTerm = { key: string; body: string };

let draftKeySeq = 0;
const newDraftKey = () => `term-${++draftKeySeq}`;

function toDrafts(snapshot: ConnectorTermsSnapshot): DraftTerm[] {
  return snapshot.terms.map((t) => ({ key: newDraftKey(), body: t.body }));
}

function formatSavedAt(iso: string | null): string {
  if (!iso) return 'Never saved';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat('en-SG', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(d);
}

/**
 * Admin editor for the Super Connector terms. Edits stay local until Save,
 * which publishes the whole list as the next version.
 */
export default function AdminTermsTab({ notify }: { notify: (m: string, s?: 'success' | 'error') => void }) {
  const [snapshot, setSnapshot] = useState<ConnectorTermsSnapshot | null>(null);
  const [drafts, setDrafts] = useState<DraftTerm[]>([]);
  const [requireReacceptance, setRequireReacceptance] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await fetchAdminTerms();
      setSnapshot(s);
      setDrafts(toDrafts(s));
      setRequireReacceptance(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load terms and conditions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const dirty = useMemo(() => {
    const saved = snapshot?.terms.map((t) => t.body) ?? [];
    const current = drafts.map((d) => d.body);
    return saved.length !== current.length || saved.some((b, i) => b !== current[i]);
  }, [snapshot, drafts]);

  const draftIssue = useMemo(() => {
    if (drafts.length === 0) return 'Add at least one term.';
    const emptyIdx = drafts.findIndex((d) => !d.body.trim());
    if (emptyIdx >= 0) return `Term ${emptyIdx + 1} is empty.`;
    const longIdx = drafts.findIndex((d) => d.body.length > TERM_BODY_MAX_CHARS);
    if (longIdx >= 0) return `Term ${longIdx + 1} is longer than ${TERM_BODY_MAX_CHARS} characters.`;
    return null;
  }, [drafts]);

  const updateDraft = (key: string, body: string) => {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, body } : d)));
  };
  const addDraft = () => setDrafts((prev) => [...prev, { key: newDraftKey(), body: '' }]);
  const removeDraft = (key: string) => setDrafts((prev) => prev.filter((d) => d.key !== key));

  const save = async () => {
    setConfirmOpen(false);
    setSaving(true);
    setError(null);
    try {
      const s = await saveAdminTerms({
        terms: drafts.map((d) => ({ body: d.body.trim() })),
        requireReacceptance,
      });
      setSnapshot(s);
      setDrafts(toDrafts(s));
      setRequireReacceptance(true);
      window.dispatchEvent(new Event('sc-terms-changed'));
      notify(requireReacceptance
        ? `Terms saved as version ${s.version}. Every user will be asked to agree again.`
        : `Terms saved as version ${s.version}. Only users who have not agreed yet will see them.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save terms and conditions');
    } finally {
      setSaving(false);
    }
  };

  const onSaveClick = () => {
    if (draftIssue) return;
    if (requireReacceptance) setConfirmOpen(true);
    else void save();
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>Terms &amp; Conditions</Typography>
        <Typography variant="body2" color="text.secondary">
          Every user must tick each term below before using Super Connector.
          {snapshot && snapshot.version > 0
            ? ` Current version ${snapshot.version}, last updated ${formatSavedAt(snapshot.updatedAt)}.`
            : ' No terms have been published yet.'}
        </Typography>
      </Box>

      {drafts.map((d, i) => (
        <Card key={d.key} variant="outlined" sx={{ bgcolor: 'var(--color-surface)' }}>
          <CardContent sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', '&:last-child': { pb: 2 } }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: ORANGE, minWidth: 24, pt: 1 }}>
              {i + 1}.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder="Term text shown next to a checkbox"
              value={d.body}
              onChange={(e) => updateDraft(d.key, e.target.value)}
              error={!d.body.trim() || d.body.length > TERM_BODY_MAX_CHARS}
              helperText={d.body.length > TERM_BODY_MAX_CHARS
                ? `${d.body.length}/${TERM_BODY_MAX_CHARS} characters`
                : undefined}
            />
            <Tooltip title="Remove term">
              <span>
                <IconButton onClick={() => removeDraft(d.key)} disabled={saving} aria-label={`Remove term ${i + 1}`}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </CardContent>
        </Card>
      ))}

      <Box>
        <Button startIcon={<AddIcon />} onClick={addDraft} disabled={saving} sx={{ textTransform: 'none', color: ORANGE }}>
          Add term
        </Button>
      </Box>

      <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid var(--color-border)' }}>
        <FormControlLabel
          control={(
            <Switch
              checked={requireReacceptance}
              onChange={(e) => setRequireReacceptance(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: ORANGE },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ORANGE },
              }}
            />
          )}
          label={<Typography variant="body2" fontWeight={600}>Require all users to re-acknowledge</Typography>}
        />
        <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-subtle)', ml: 6 }}>
          {requireReacceptance
            ? 'On: everyone, including users who already agreed, will see the terms again with a notice that they were updated.'
            : 'Off: only users who have never agreed will see the terms. Users who already agreed are not asked again.'}
        </Typography>
      </Box>

      {draftIssue && dirty && <Alert severity="error" sx={{ py: 0.5 }}>{draftIssue}</Alert>}
      {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={() => { void load(); }} disabled={saving || !dirty} sx={{ textTransform: 'none' }}>
          Discard changes
        </Button>
        <Button
          variant="contained"
          onClick={onSaveClick}
          disabled={saving || !dirty || !!draftIssue}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', minWidth: 120 }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Ask everyone to agree again?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Every Super Connector user, including those who already agreed, will see the updated terms the next
            time they open the app and must agree before continuing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { void save(); }}
            sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            Save and require agreement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
